import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
// @ts-ignore
import { __serializeForClipboard as serializeForClipboard } from "@tiptap/pm/view";
import styles from "./DragHandle.module.css";

function createDragHandle(editorView: EditorView, position: string): HTMLElement {
  function dragStart(event: DragEvent, view: EditorView) {
    if (!event.dataTransfer) {
      return;
    }

    const coords = { left: event.clientX + 50, top: event.clientY };
    const pos = blockPosAtCoords(coords, view);

    if (pos != null) {
      view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));

      const slice = view.state.selection.content();
      const { dom, text } = serializeForClipboard(view, slice);

      event.dataTransfer.clearData();
      event.dataTransfer.setData("text/html", dom.innerHTML);
      event.dataTransfer.setData("text/plain", text);

      const element: Element = document.querySelector(".ProseMirror-selectednode")!;
      event.dataTransfer?.setDragImage(element, 0, 0);
      view.dragging = { slice, move: true };
    }
  }

  const element = document.createElement("div");
  element.draggable = true;
  element.classList.add(position);
  element.classList.add(styles.globalDragHandle);
  element.addEventListener("dragstart", (e) => dragStart(e, editorView));
  document.body.appendChild(element);

  return element;
}

function deleteDragHandle(node: Element | null) {
  if (node) node.remove();
}

function getGrabbableNode(childNode: Element): Element | undefined {
  let node: Element = childNode;

  // find grabbable node (we don't want to grab text or tiny nodes, just li or larger)
  while (node && node.localName !== "li" && node.parentNode) {
    // if contents block is wrapping all the other nodes, we don't want to continue going up
    if ((node.parentNode.parentNode as Element)?.classList?.contains("content-block-wrapper")) {
      break;
    }
    // if we reach the top of the contents block, we don't want to continue going up
    if ((node.parentNode as Element)?.classList?.contains("content-block-wrapper")) {
      break;
    }
    if ((node.parentNode as Element).classList?.contains("ProseMirror")) {
      // reached the top, so this node will do
      break;
    }
    node = node.parentNode as Element;
  }
  // don't allow grab if no-grab attribute is set or is top-level div
  if ((node && node.attributes?.getNamedItem("no-grab")) || node.localName === "div") return;
  return node;
}

function blockPosAtCoords(coords: { left: number; top: number }, view: any) {
  const pos = view.posAtCoords(coords);
  if (!pos) return null;

  let node: any = view.domAtPos(pos.pos);

  if (!node) return null;

  node = getGrabbableNode(node.node);

  if (node && node.nodeType === 1) {
    const desc = view.docView.nearestDesc(node, true);

    if (desc && desc !== view.docView) {
      return desc.posBefore;
    }
  }
  return null;
}

export default Extension.create({
  addOptions() {
    return {
      position: "absolute",
    };
  },
  addProseMirrorPlugins() {
    let dropElement: HTMLElement | null;
    const PADDING = 16;
    const { position } = this.options;
    return [
      new Plugin({
        view(editorView) {
          dropElement = createDragHandle(editorView, position);

          return {
            update(view) {
              if (!view.editable && dropElement) {
                deleteDragHandle(dropElement);
                dropElement = null;
              }
            },
            destroy() {
              deleteDragHandle(dropElement);
              dropElement = null;
            },
          };
        },
        props: {
          // @ts-ignore
          handleDOMEvents: {
            drop() {
              setTimeout(() => {
                const node = document.querySelector(".ProseMirror-hideselection");
                if (node) {
                  node.classList.remove("ProseMirror-hideselection");
                }
              }, 50);
              return false;
            },
            mousemove(view, event: MouseEvent) {
              // disable if document is read only
              if (!view.editable) return false;

              const coords = {
                left: event.clientX + PADDING + 50,
                top: event.clientY,
              };

              const pos = view.posAtCoords(coords);

              if (pos) {
                let node: any = view.domAtPos(pos?.pos);

                if (node) {
                  node = getGrabbableNode(node.node);

                  if (!node) {
                    deleteDragHandle(dropElement);
                    dropElement = null;
                    return false;
                  }
                  // recreate handle if removed
                  if (!dropElement) {
                    dropElement = createDragHandle(view, position);
                  }

                  if (dropElement && node instanceof Element) {
                    const { top, left } = node.getBoundingClientRect();

                    const win = node.ownerDocument.defaultView;
                    const rect = { top, left };

                    if (position !== "fixed") {
                      rect.top += win?.scrollY || 0;
                      rect.left += win?.scrollX || 0;
                    }

                    dropElement.style.left = `${-PADDING + rect.left}px`;
                    dropElement.style.top = `${rect.top}px`;
                    dropElement.style.display = "block";
                  } else {
                    // we're not over a valid (possibly react) node, so make handle disappear
                    deleteDragHandle(dropElement);
                    dropElement = null;
                  }
                }
              }
              return false;
            },
            mousewheel: () => {
              // removes the drag handle when scrolling to prevent it from being stuck in one position when the window is scrolled
              deleteDragHandle(dropElement);
              dropElement = null;
            },
          },
        },
      }),
    ];
  },
});
